<template>
    <div class="mx-2 mx-lg-5">
        <div class="card mt-3">
            <div class="card-header d-flex justify-content-between align-items-center">
                <h3>{{ gatehouse.name }}</h3>
                <div>
                    <button class="btn btn-sm btn-secondary me-3" @click="editGatehouse">
                        <icon icon="EditPen"></icon>
                    </button>
                    <button class="btn btn-sm btn-danger" @click="deleteGatehouse">
                        <icon icon="Trash"></icon>
                    </button>
                </div>
            </div>
            <div class="card-body">
                <div class="row">
                    <div class="col-md-6">
                        <dl class="row mb-0">
                            <dt class="col-sm-4">{{ $t("gatehouse.name") }}</dt>
                            <dd class="col-sm-8">{{ gatehouse.name }}</dd>

                            <dt class="col-sm-4">{{ $t("gatehouse.address") }}</dt>
                            <dd class="col-sm-8">{{ gatehouse.address }}</dd>
                        </dl>
                    </div>
                    <div class="col-md-6">
                        <dl class="row mb-0">
                            <dt class="col-sm-4">{{ $t("gatehouse.qr") }}</dt>
                            <dd class="col-sm-8">
                                <img
                                    v-if="gatehouse.qr_img"
                                    :src="getQr"
                                    alt="QR kód"
                                    width="180"
                                />
                            </dd>
                        </dl>
                    </div>
                </div>
            </div>
        </div>

        <div class="card mt-3">
            <div class="card-header">
                <h5 class="mb-0">{{ $t("gatehouse.settings") }}</h5>
            </div>
            <div class="card-body">
                <div class="form-check form-switch mb-3 filter_switch">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        id="carrierMultiselect"
                        :checked="gatehouse.carrier_multiselect"
                        @change="toggleCarrierMultiselect"
                    >
                    <label class="form-check-label fw-bold" for="carrierMultiselect">
                        <span>{{ $t("gatehouse.carrierMultiselect") }}</span>
                        <span>
                            <small class="text-muted ms-1">
                                ({{ $t("gatehouse.carrierMultiselectDesc") }})
                            </small>
                        </span>
                    </label>
                </div>
                <div class="form-check form-switch mb-3 filter_switch">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        id="guestbookNameMultiselect"
                        :checked="gatehouse.guestbook_name_multiselect"
                        @change="toggleGuestbookNameMultiselect"
                    >
                    <label class="form-check-label fw-bold" for="guestbookNameMultiselect">
                        <span>{{ $t("gatehouse.guestbookNameMultiselect") }}</span>
                        <span>
                            <small class="text-muted ms-1">
                                ({{ $t("gatehouse.guestbookNameMultiselectDesc") }})
                            </small>
                        </span>
                    </label>
                </div>
            </div>
        </div>

        <div class="card mt-3">
            <div class="card-header">
                <h5 class="mb-0">{{ $t("gatehouse.exports.title") }}</h5>
            </div>
            <div class="card-body">
                <button class="btn btn-sm btn-outline-primary mb-3" @click="openExportModal">
                    {{ $t("gatehouse.exports.manualExport") }}
                </button>
                <div
                    v-for="(exportConfig, key) in exports"
                    :key="key"
                    class="export-section mb-4"
                    :class="{ 'pb-4 border-bottom': key !== lastExportKey }"
                >
                    <div class="form-check form-switch mb-3 filter_switch">
                        <input
                            class="form-check-input"
                            type="checkbox"
                            :id="`export_${key}`"
                            :checked="exportConfig.enabled"
                            @change="toggleExportEnabled(key)"
                        >
                        <label class="form-check-label fw-bold" :for="`export_${key}`">
                            {{ $t(`gatehouse.exports.${key}`) }}
                        </label>
                    </div>

                    <div v-if="exportConfig.enabled" class="ms-5">
                        <div class="d-flex gap-3 mb-3">
                            <div v-for="freq in ['daily', 'weekly', 'monthly']" :key="freq" class="form-check">
                                <input
                                    class="form-check-input"
                                    type="radio"
                                    :id="`freq_${key}_${freq}`"
                                    :name="`freq_${key}`"
                                    :value="freq"
                                    :checked="exportConfig.frequency === freq"
                                    @change="setExportFrequency(key, freq)"
                                >
                                <label class="form-check-label" :for="`freq_${key}_${freq}`">
                                    {{ $t(`gatehouse.exports.frequency.${freq}`) }}
                                </label>
                            </div>
                        </div>
                        <div
                            v-for="(emailObj, idx) in exportConfig.emails"
                            :key="idx"
                            class="d-flex align-items-center gap-2 mb-2"
                        >
                            <span style="min-width: 200px;">{{ emailObj.email }}</span>
                            <input
                                type="text"
                                class="form-control form-control-sm"
                                style="max-width: 200px;"
                                :placeholder="$t('gatehouse.exports.passwordPlaceholder')"
                                v-model="emailObj.pdf_password"
                                @change="saveExportEmails(key)"
                            >
                            <button
                                class="btn btn-outline-danger btn-sm text-nowrap"
                                @click="removeEmail(key, idx)"
                            >
                                {{ $t("utils.delete") }}
                            </button>
                        </div>

                        <div v-if="exportConfig.addingEmail" class="d-flex gap-2 mt-2 flex-wrap" style="max-width: 620px;">
                            <input
                                type="email"
                                class="form-control form-control-sm"
                                style="max-width: 240px;"
                                :placeholder="$t('gatehouse.exports.emailPlaceholder')"
                                v-model="exportConfig.newEmail"
                                @keyup.enter="addEmail(key)"
                                @keyup.esc="cancelAddEmail(key)"
                                ref="emailInput"
                                autofocus
                            >
                            <input
                                type="text"
                                class="form-control form-control-sm"
                                style="max-width: 200px;"
                                :placeholder="$t('gatehouse.exports.passwordPlaceholder')"
                                v-model="exportConfig.newPassword"
                                @keyup.enter="addEmail(key)"
                                @keyup.esc="cancelAddEmail(key)"
                            >
                            <button
                                class="btn btn-sm btn-primary text-nowrap"
                                @click="addEmail(key)"
                                :disabled="!exportConfig.newEmail"
                            >
                                {{ $t("gatehouse.exports.addEmail") }}
                            </button>
                            <button
                                class="btn btn-sm btn-outline-secondary"
                                @click="cancelAddEmail(key)"
                            >
                                {{ $t("utils.cancel") }}
                            </button>
                        </div>
                        <button
                            v-if="!exportConfig.addingEmail"
                            class="btn btn-sm btn-outline-primary mt-1"
                            @click="exportConfig.addingEmail = true"
                        >
                            + {{ $t("gatehouse.exports.addEmail") }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Export modal -->
    <div class="modal fade" tabindex="-1" aria-hidden="true" ref="exportModal">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
                <div class="modal-header justify-content-between align-items-center border-0 mx-2">
                    <h5 class="modal-title">{{ $t("gatehouse.exports.manualExport") }}</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body mx-2">
                    <div class="mb-3">
                        <label class="form-label fw-bold">{{ $t("gatehouse.exports.exportType") }}</label>
                        <select class="form-select" v-model="exportForm.type">
                            <option value="patrol_log">{{ $t("gatehouse.exports.patrolLog") }}</option>
                            <option value="guestbook">{{ $t("gatehouse.exports.guestbook") }}</option>
                            <option value="vehicle_entry">{{ $t("gatehouse.exports.vehicleEntry") }}</option>
                        </select>
                    </div>

                    <div class="mb-3 d-flex gap-2">
                        <button class="btn btn-sm btn-outline-secondary" @click="setExportToday">
                            {{ $t("gatehouse.exports.today") }}
                        </button>
                        <button class="btn btn-sm btn-outline-secondary" @click="setExportYesterday">
                            {{ $t("gatehouse.exports.yesterday") }}
                        </button>
                    </div>

                    <div class="row g-2">
                        <div class="col-6">
                            <label class="form-label">{{ $t("gatehouse.exports.dateFrom") }}</label>
                            <input type="date" class="form-control" v-model="exportForm.dateFrom">
                        </div>
                        <div class="col-6">
                            <label class="form-label">{{ $t("gatehouse.exports.dateTo") }}</label>
                            <input type="date" class="form-control" v-model="exportForm.dateTo">
                        </div>
                    </div>
                </div>
                <div class="modal-footer border-0">
                    <button
                        class="btn btn-primary"
                        @click="generateExport"
                        :disabled="!exportForm.dateFrom || !exportForm.dateTo || exportLoading"
                    >
                        <span v-if="exportLoading" class="spinner-border spinner-border-sm me-1"></span>
                        {{ $t("gatehouse.exports.generate") }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>


<script>
import { CONFIG } from "@/config.js"
import Icon from '@/components/Icon.vue';
import { Modal } from 'bootstrap';

export default {
    components: {
        Icon,
    },
    data() {
        return {
            gatehouse: {},
            exports: {
                guestbook:    { enabled: false, emails: [], newEmail: '', newPassword: '', addingEmail: false, apiKey: 'guestbook', frequency: 'daily' },
                vehicleEntry: { enabled: false, emails: [], newEmail: '', newPassword: '', addingEmail: false, apiKey: 'vehicle_entry', frequency: 'daily' },
                patrolLog:    { enabled: false, emails: [], newEmail: '', newPassword: '', addingEmail: false, apiKey: 'patrol_log', frequency: 'daily' },
            },
            exportModal: null,
            exportLoading: false,
            exportForm: {
                type: 'patrol_log',
                dateFrom: '',
                dateTo: '',
            },
        }
    },
    mounted() {
        this.exportModal = new Modal(this.$refs.exportModal);
    },
    methods: {
        getGatehouse() {
            this.$store.getters.api.get(`/dashboard/${this.$route.params.slug}/gatehouse`)
            .then(response => {
                this.gatehouse = response.data;
                this.loadExports();
            })
            .catch(error => {
                console.error("Error loading gatehouse. ", error)
            });
        },
        loadExports() {
            for (const config of Object.values(this.exports)) {
                config.enabled = this.gatehouse[`${config.apiKey}_export_enabled`] ?? false;
                config.emails = [...(this.gatehouse[`${config.apiKey}_export_emails`] ?? [])];
                config.frequency = this.gatehouse[`${config.apiKey}_export_frequency`] ?? 'daily';
            }
        },
        editGatehouse() {
            this.$router.push({
                name: "GatehouseEditView",
                params: {
                    slug: this.$route.params.slug,
                    id: this.gatehouseId
                }
            });
        },
        toggleGuestbookNameMultiselect() {
            const newValue = !this.gatehouse.guestbook_name_multiselect;
            this.$store.getters.api.patch(`/dashboard/${this.$route.params.slug}/gatehouse/settings/edit`, {
                guestbook_name_multiselect: newValue
            })
            .then(() => {
                this.gatehouse.guestbook_name_multiselect = newValue;
                this.$store.commit("updateGatehouseSettings", {
                    slug: this.$route.params.slug,
                    settings: { guestbook_name_multiselect: newValue }
                });
            })
            .catch(error => {
                console.error("Error updating gatehouse settings.", error);
            });
        },
        toggleCarrierMultiselect() {
            const newValue = !this.gatehouse.carrier_multiselect;
            this.$store.getters.api.patch(`/dashboard/${this.$route.params.slug}/gatehouse/settings/edit`, {
                carrier_multiselect: newValue
            })
            .then(() => {
                this.gatehouse.carrier_multiselect = newValue;
                this.$store.commit("updateGatehouseSettings", {
                    slug: this.$route.params.slug,
                    settings: { carrier_multiselect: newValue }
                });
            })
            .catch(error => {
                console.error("Error updating gatehouse settings.", error);
            });
        },
        setExportFrequency(key, frequency) {
            const config = this.exports[key];
            this.$store.getters.api.patch(`/dashboard/${this.$route.params.slug}/gatehouse/settings/edit`, {
                [`${config.apiKey}_export_frequency`]: frequency,
            })
            .then(() => {
                config.frequency = frequency;
            })
            .catch(error => {
                console.error("Error updating export frequency.", error);
            });
        },
        toggleExportEnabled(key) {
            const config = this.exports[key];
            const newValue = !config.enabled;
            this.$store.getters.api.patch(`/dashboard/${this.$route.params.slug}/gatehouse/settings/edit`, {
                [`${config.apiKey}_export_enabled`]: newValue,
            })
            .then(() => {
                config.enabled = newValue;
            })
            .catch(error => {
                console.error("Error updating export settings.", error);
            });
        },
        saveExportEmails(key) {
            const config = this.exports[key];
            this.$store.getters.api.patch(`/dashboard/${this.$route.params.slug}/gatehouse/settings/edit`, {
                [`${config.apiKey}_export_emails`]: config.emails,
            })
            .catch(error => {
                console.error("Error saving export emails.", error);
            });
        },
        addEmail(key) {
            const email = this.exports[key].newEmail.trim();
            if (!email) return;
            this.exports[key].emails.push({ email, pdf_password: this.exports[key].newPassword.trim() });
            this.exports[key].newEmail = '';
            this.exports[key].newPassword = '';
            this.exports[key].addingEmail = false;
            this.saveExportEmails(key);
        },
        cancelAddEmail(key) {
            this.exports[key].newEmail = '';
            this.exports[key].newPassword = '';
            this.exports[key].addingEmail = false;
        },
        removeEmail(key, idx) {
            this.exports[key].emails.splice(idx, 1);
            this.saveExportEmails(key);
        },
        openExportModal() {
            this.exportModal.show();
        },
        setExportToday() {
            const today = new Date().toISOString().split('T')[0];
            this.exportForm.dateFrom = today;
            this.exportForm.dateTo = today;
        },
        setExportYesterday() {
            const d = new Date();
            d.setDate(d.getDate() - 1);
            const yesterday = d.toISOString().split('T')[0];
            this.exportForm.dateFrom = yesterday;
            this.exportForm.dateTo = yesterday;
        },
        generateExport() {
            this.exportLoading = true;
            this.$store.getters.api.get(
                `/dashboard/${this.$route.params.slug}/gatehouse/export`,
                {
                    params: {
                        type: this.exportForm.type,
                        date_from: this.exportForm.dateFrom,
                        date_to: this.exportForm.dateTo,
                    },
                    responseType: 'blob',
                }
            )
            .then(response => {
                const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                const a = document.createElement('a');
                a.href = url;
                a.download = `export_${this.exportForm.type}_${this.exportForm.dateFrom}_${this.exportForm.dateTo}.pdf`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                this.exportModal.hide();
            })
            .catch(error => {
                console.error('Export error:', error);
            })
            .finally(() => {
                this.exportLoading = false;
            });
        },
        deleteGatehouse() {
            const confirm = window.confirm(this.$t("utils.deleteConfirm", { name: this.gatehouse.name }));
            if (!confirm) return;

            this.$store.getters.api.delete(`/dashboard/${this.$route.params.slug}/gatehouse/${this.gatehouseId}/delete`)
            .then(() => {
                this.$store.commit("removeGatehouse", this.gatehouse.slug);

                const slug = this.$store.getters.gatehouses[0].slug;
                this.$router.push({
                    name: "GatehouseView",
                    params: {
                        slug: slug,
                    }
                });
            })
            .catch(error => {
                console.error("Error deleting gatehouse.", error);
            });
        },
    },
    computed: {
        getQr() {
            const slug = this.$route.params.slug;
            const url = CONFIG.api.baseUrl;
            return `${url}/dashboard/${slug}/gatehouse/qr-code-img`;
        },
        gatehouseId() {
            return this.gatehouse.id;
        },
        lastExportKey() {
            const keys = Object.keys(this.exports);
            return keys[keys.length - 1];
        }
    },
    watch: {
        "$route.params.slug": {
            immediate: true,
            handler(newSlug) {
                this.getGatehouse();
            }
        }
    }
}
</script>

<style scoped>
.filter_switch {
    .form-check-input {
        width: 40px;
        height: 20px;
        cursor: pointer;
    }
    .form-check-label {
        margin-left: 12px;
        font-size: 16px;
        cursor: pointer;
    }
}
</style>